import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  ViewChild,
  ChangeDetectorRef,
  Input,
  signal,
  computed,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Group, Player, GroupSlot, ScorecardResponse } from '../../models';
import { PlayerService, ScorecardService, SortConfig, TableSortingService } from '../../services';
import { forkJoin } from 'rxjs';
import { SortableHeaderComponent } from '../shared/sortable-header/sortable-header.component';

@Component({
  selector: 'app-player-search-modal',
  standalone: true,
  imports: [FormsModule, SortableHeaderComponent],
  templateUrl: './player-search-modal.component.html',
  styleUrls: ['./player-search-modal.component.scss'],
})
export class PlayerSearchModalComponent {
  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;

  // Signals for reactive state management
  private players = signal<SearchPlayer[]>([]);
  sortConfig = signal<SortConfig | null>(null);
  searchTerm = signal<string>('');

  private _groups: Group[] = [];
  @Input()
  get groups(): Group[] {
    return this._groups;
  }
  set groups(value: Group[]) {
    this._groups = value;
    this.load();
  }

  // Computed filtered and sorted players
  filteredPlayers = computed(() => {
    const allPlayers = this.players();
    const searchTerm = this.searchTerm();
    const sort = this.sortConfig();

    // First filter by search term
    let filtered = searchTerm
      ? allPlayers.filter(
        (player) =>
          player.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          player.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : allPlayers;

    // Then apply sorting if configured
    if (sort) {
      filtered = this.tableSortingService.sort(filtered, sort.key, sort.direction);
    }

    return filtered;
  });


  isVisible = false;

  @Output() playerSelected = new EventEmitter<SearchPlayer>();

  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    this.closeModal();
  }

  constructor(
    private playerService: PlayerService,
    private scorecardService: ScorecardService,
    private cdr: ChangeDetectorRef,
    private tableSortingService: TableSortingService
  ) { }

  private load(): void {
    if (!this.groups || this.groups.length === 0) {
      this.players.set([]);
      return;
    }

    forkJoin({
      players: this.playerService.getAll(),
      scorecards: this.scorecardService.getByRound(this.groups[0].roundId!),
    }).subscribe(({ players, scorecards }) => {
      const searchPlayers = players as SearchPlayer[];
      searchPlayers.forEach((player: SearchPlayer) => {
        player.scorecard =
          scorecards.find((sc) => sc.playerId === player.playerId) || null;
        player.slot = null;
        if (player.scorecard && this.groups && this.groups.length > 0) {
          for (const group of this.groups) {
            if (group.slots && Array.isArray(group.slots)) {
              const foundSlot = (group.slots as GroupSlot[]).find(
                (slot) => slot.scorecardId === player.scorecard!.scorecardId
              );
              if (foundSlot) {
                player.slot = foundSlot;
                break;
              }
            }
          }
        }
      });

      const filteredPlayers = searchPlayers
        .filter((p: SearchPlayer) => p.slot !== null)
        .sort((a: SearchPlayer, b: SearchPlayer) => a.lastName!.localeCompare(b.lastName!));

      this.players.set(filteredPlayers);
    });
  }

  filterPlayers(): void {
    // This is now handled by the computed signal, but we keep it for compatibility
    // The computed signal will automatically update when searchTerm changes
  }

  openModal(): void {
    this.isVisible = true;
    this.searchTerm.set('');

    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      if (this.searchInput?.nativeElement) {
        this.searchInput.nativeElement.focus({ preventScroll: true });
      } else {
        const el = document.getElementById('searchInput') || document.querySelector('input[data-cy="searchInput"]');
        if (el) (el as HTMLElement).focus();
      }
    });
  }

  closeModal(): void {
    this.isVisible = false;
  }

  selectPlayer(player: SearchPlayer): void {
    this.playerSelected.emit(player);
    this.closeModal();
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.filterPlayers();
    }
  }

  onSort(event: { key: string; direction: 'asc' | 'desc' }) {
    this.sortConfig.set({ key: event.key, direction: event.direction });
  }
}

// Add a type for Player with its Scorecard and GroupSlot
export type SearchPlayer = Player & {
  scorecard: ScorecardResponse | null;
  slot: GroupSlot | null;
};
